import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { trpc } from "@/lib/trpc";
import { useI18n } from "@/lib/i18n";
import { Loader2, Plug, RefreshCw, Webhook } from "lucide-react";
import { toast } from "sonner";

type Provider =
  | "github"
  | "google_calendar"
  | "slack"
  | "microsoft"
  | "exchange_on_prem";

const CREDENTIAL_PROVIDERS = new Set<Provider>(["exchange_on_prem"]);

/**
 * Web v1 compliance gate: username/password EWS credential intake is
 * DISABLED — no username/password fields, no Connect button. The server
 * route (/api/integrations/exchange_on_prem/connect) is gated too, so this
 * is presentation of a server rule, not the rule itself. Microsoft 365
 * OAuth is the supported path.
 */
const EXCHANGE_CREDENTIALS_DISABLED = true;

function formatRelative(d: Date | string, locale: "en" | "ar") {
  const date = typeof d === "string" ? new Date(d) : d;
  const diffMs = Date.now() - date.getTime();
  const min = Math.round(diffMs / 60000);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  if (min < 1) return locale === "ar" ? "الآن" : "just now";
  if (min < 60) return rtf.format(-min, "minute");
  const hr = Math.round(min / 60);
  if (hr < 24) return rtf.format(-hr, "hour");
  const day = Math.round(hr / 24);
  return rtf.format(-day, "day");
}

function syncedMessage(inserted: number, locale: "en" | "ar") {
  if (locale === "ar") {
    // Arabic count agreement: 1 / 2 / 3-10 / 11+ each take a different noun form.
    if (inserted === 0) return "تمت المزامنة. لم يُسحب أي عنصر";
    if (inserted === 1) return "تمت المزامنة. سُحب عنصر واحد";
    if (inserted === 2) return "تمت المزامنة. سُحب عنصران";
    if (inserted <= 10) return `تمت المزامنة. سُحبت ${inserted} عناصر`;
    return `تمت المزامنة. سُحب ${inserted} عنصرًا`;
  }
  return `Synced. Pulled ${inserted} item${inserted === 1 ? "" : "s"}`;
}

const META: Record<Provider, { label: string; emoji: string; description: string }> = {
  github:           { label: "GitHub",           emoji: "🐙", description: "Commits, PRs, reviews" },
  google_calendar:  { label: "Google Calendar",  emoji: "📅", description: "Meetings & events"     },
  slack:            { label: "Slack",            emoji: "💬", description: "DMs & @mentions"      },
  microsoft:        { label: "Outlook & Microsoft 365", emoji: "🅼", description: "Calendar & sent mail (Graph)" },
  exchange_on_prem: { label: "Exchange (on-prem)", emoji: "🏢", description: "Self-hosted Exchange via EWS" },
};

export function IntegrationsDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (b: boolean) => void }) {
  const { t, locale } = useI18n();
  const list = trpc.integrations.list.useQuery(undefined, { enabled: open });
  const disconnect = trpc.integrations.disconnect.useMutation({
    onSuccess: () => list.refetch(),
  });

  function startConnect(provider: Provider) {
    const origin = window.location.origin;
    window.location.href = `/api/integrations/${provider}/start?origin=${encodeURIComponent(origin)}`;
  }

  async function syncNow(provider: Provider) {
    const r = await fetch(`/api/integrations/${provider}/sync`, { method: "POST", credentials: "include" });
    if (!r.ok) {
      const txt = await r.text();
      toast.error(locale === "ar" ? `فشلت المزامنة: ${txt}` : `Sync failed: ${txt}`);
      return;
    }
    const j = (await r.json()) as { inserted: number };
    toast.success(syncedMessage(j.inserted, locale));
    list.refetch();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t.integrations}</DialogTitle>
          <DialogDescription>
            {locale === "ar"
              ? "اربط أدواتك. سنقوم بسحب العمل تلقائياً."
              : "Connect your tools. We'll pull your work automatically."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 mt-2">
          {list.isLoading && (
            <div className="flex items-center justify-center gap-2 text-muted-foreground py-6">
              <Loader2 className="size-4 animate-spin" />
              <span className="text-xs">{locale === "ar" ? "تحميل…" : "Loading…"}</span>
            </div>
          )}
          {list.error && (
            <div className="rounded-xl border border-red-200 bg-red-50 dark:bg-red-950/20 p-4 text-sm text-red-600">
              <div className="font-medium mb-1">
                {locale === "ar" ? "تعذّر تحميل التكاملات" : "Couldn't load integrations"}
              </div>
              <div className="text-xs text-muted-foreground">{list.error.message}</div>
              <Button size="sm" variant="outline" className="mt-3" onClick={() => list.refetch()}>
                {locale === "ar" ? "إعادة المحاولة" : "Try again"}
              </Button>
            </div>
          )}
          {!list.isLoading && !list.error && (list.data?.length ?? 0) === 0 && (
            <div className="text-sm text-muted-foreground p-6 text-center border rounded-xl">
              {locale === "ar"
                ? "لم تظهر أي إدخالات بعد. أعد تحميل الصفحة."
                : "No integrations to show. Reload the page."}
            </div>
          )}
          {list.data?.map(integ => {
            const m = META[integ.provider as Provider];
            return (
              <div
                key={integ.provider}
                className="rounded-xl border bg-card px-4 py-3"
              >
                <div className="flex items-center gap-3">
                  <div className="text-2xl">{m.emoji}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium leading-tight flex items-center gap-1.5">
                      {m.label}
                      {integ.errored && (
                        <span
                          className="inline-block h-2 w-2 rounded-full bg-amber-500"
                          aria-label={locale === "ar" ? "خطأ في المزامنة" : "Sync error"}
                          title={integ.lastErrorMessage ?? undefined}
                        />
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {integ.connected
                        ? `${locale === "ar" ? "متصل" : "Connected"} · ${integ.accountLabel ?? "active"}${
                            integ.lastSyncAt
                              ? ` · ${locale === "ar" ? "آخر مزامنة" : "synced"} ${formatRelative(integ.lastSyncAt, locale)}`
                              : ` · ${locale === "ar" ? "لم تتم المزامنة بعد" : "not synced yet"}`
                          }`
                        : integ.errored
                          ? `${locale === "ar" ? "تعذّرت آخر مزامنة" : "last sync failed"}${
                              integ.lastErrorAt
                                ? ` · ${formatRelative(integ.lastErrorAt, locale)}`
                                : ""
                            }`
                          : !integ.configured
                            ? t.comingSoon
                            : m.description}
                    </div>
                  </div>

                  {integ.connected ? (
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="ghost" onClick={() => syncNow(integ.provider as Provider)}>
                        <RefreshCw className="size-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => disconnect.mutate({ provider: integ.provider as Provider })}
                      >
                        {t.disconnect}
                      </Button>
                    </div>
                  ) : integ.errored ? (
                    <Button
                      size="sm"
                      onClick={() =>
                        CREDENTIAL_PROVIDERS.has(integ.provider as Provider)
                          ? null
                          : startConnect(integ.provider as Provider)
                      }
                      className="gap-1.5 bg-amber-500 hover:bg-amber-500/90 text-white"
                    >
                      <Plug className="size-3.5" />
                      {locale === "ar" ? "إعادة الربط" : "Reconnect"}
                    </Button>
                  ) : CREDENTIAL_PROVIDERS.has(integ.provider as Provider) ? (
                    EXCHANGE_CREDENTIALS_DISABLED ? (
                      <span className="text-xs text-muted-foreground" data-testid="exchange-disabled-badge">
                        {locale === "ar" ? "معطّل" : "Disabled"}
                      </span>
                    ) : (
                      // Credential providers (EWS) connect via an inline form,
                      // not a redirect — the form lives below the row.
                      <span className="text-xs text-muted-foreground">
                        {locale === "ar" ? "أدخل بيانات الاعتماد ←" : "Use form ↓"}
                      </span>
                    )
                  ) : (
                    <Button
                      size="sm"
                      disabled={!integ.configured}
                      onClick={() => startConnect(integ.provider as Provider)}
                      className="gap-1.5"
                    >
                      <Plug className="size-3.5" />
                      {t.connect}
                    </Button>
                  )}
                </div>

                {!integ.connected &&
                  CREDENTIAL_PROVIDERS.has(integ.provider as Provider) &&
                  (EXCHANGE_CREDENTIALS_DISABLED ? (
                    <div
                      className="mt-3 rounded-lg border border-border bg-muted/40 px-3 py-2.5 text-xs text-muted-foreground"
                      data-testid="exchange-disabled-note"
                    >
                      {locale === "ar"
                        ? "ربط Exchange عبر اسم المستخدم وكلمة المرور معطّل في الإصدار الأول. استخدم Outlook و Microsoft 365 بدلاً منه، أو تواصل مع المسؤول."
                        : "Exchange EWS (username/password) connection is disabled for Web v1. Use Outlook & Microsoft 365 instead, or contact your admin."}
                    </div>
                  ) : (
                    <ExchangeConnectForm onConnected={() => list.refetch()} />
                  ))}

                {integ.connected && integ.integrationId && (
                  <WebhookSecretEditor
                    provider={integ.provider as Provider}
                    integrationId={integ.integrationId}
                    configured={integ.webhookConfigured}
                    onChanged={() => list.refetch()}
                  />
                )}
              </div>
            );
          })}
        </div>

        <p className="text-xs text-muted-foreground mt-4 leading-relaxed">
          {locale === "ar"
            ? "تُحفظ بيانات الاعتماد بصورة مشفّرة. يمكنك الفصل في أي وقت."
            : "We store credentials encrypted and only read activity data. Disconnect anytime."}
        </p>

        <TeamDigestSection />
      </DialogContent>
    </Dialog>
  );
}

/**
 * Team-level digest configuration covering three channels:
 *   • Slack    — channel ID + owner's bot token
 *   • Email    — list of recipient addresses
 *   • Webhook  — any URL accepting JSON POSTs
 *
 * Each channel has independent daily / weekly toggles. Owner-only
 * (server enforces); the section returns null for non-owners so
 * managers don't see incomplete controls they can't save.
 */
function TeamDigestSection() {
  const { locale } = useI18n();
  const teamMineQ = trpc.team.mine.useQuery();
  const myEntry = teamMineQ.data?.[0] as
    | { team?: { id?: number; name?: string }; role?: string }
    | undefined;
  const isOwner = myEntry?.role === "owner";
  const teamId = myEntry?.team?.id;

  const configQ = trpc.team.digestConfig.useQuery(
    { teamId: teamId ?? -1 },
    { enabled: !!teamId && isOwner, staleTime: 30_000 },
  );
  const setConfig = trpc.team.setDigestConfig.useMutation();
  // Two distinct actions, deliberately: `preview` renders and sends nothing;
  // `sendTest` really delivers and is only reachable from the preview panel.
  const preview = trpc.team.sendDigestPreview.useMutation();
  const sendTest = trpc.team.sendDigestTest.useMutation();
  const [previewOf, setPreviewOf] = useState<{
    channel: "slack" | "email" | "webhook";
    kind: "daily" | "weekly";
    subject: string;
    text: string;
    truncated: boolean;
    recipientCount: number;
    targetConfigured: boolean;
  } | null>(null);
  const utils = trpc.useUtils();

  // Local form state — one block per channel. Hydrated from
  // server response in the effect below.
  const [slackChannelId, setSlackChannelId] = useState("");
  const [slackDaily, setSlackDaily] = useState(false);
  const [slackWeekly, setSlackWeekly] = useState(false);
  const [emailDaily, setEmailDaily] = useState(false);
  const [emailWeekly, setEmailWeekly] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookDaily, setWebhookDaily] = useState(false);
  const [webhookWeekly, setWebhookWeekly] = useState(false);

  useEffect(() => {
    if (!configQ.data) return;
    const c = configQ.data;
    setSlackChannelId(c.slackChannelId ?? "");
    setSlackDaily(!!c.slackDaily);
    setSlackWeekly(!!c.slackWeekly);
    setEmailDaily(!!c.emailDaily);
    setEmailWeekly(!!c.emailWeekly);
    setWebhookUrl(c.webhookUrl ?? "");
    setWebhookDaily(!!c.webhookDaily);
    setWebhookWeekly(!!c.webhookWeekly);
  }, [configQ.data]);

  if (!isOwner || !teamId) return null;

  async function save() {
    if (!teamId) return;
    const slackId = slackChannelId.trim();
    const hookUrl = webhookUrl.trim();
    try {
      await setConfig.mutateAsync({
        teamId,
        slack: {
          channelId: slackId.length === 0 ? null : slackId,
          dailyEnabled: slackDaily,
          weeklyEnabled: slackWeekly,
        },
        email: {
          dailyEnabled: emailDaily,
          weeklyEnabled: emailWeekly,
        },
        webhook: {
          url: hookUrl.length === 0 ? null : hookUrl,
          dailyEnabled: webhookDaily,
          weeklyEnabled: webhookWeekly,
        },
      });
      utils.team.digestConfig.invalidate();
      toast.success(locale === "ar" ? "تمّ الحفظ" : "Saved");
    } catch (e: any) {
      toast.error(e?.message ?? "Couldn't save");
    }
  }

  /** Renders the digest. Sends nothing — delivery needs `confirmSend` below. */
  async function openPreview(
    channel: "slack" | "email" | "webhook",
    kind: "daily" | "weekly",
  ) {
    if (!teamId) return;
    try {
      const r = await preview.mutateAsync({ teamId, channel, kind });
      if (!r.ok) {
        toast.warning(
          locale === "ar"
            ? `لا توجد بيانات: ${(r as any).reason ?? "غير معروف"}`
            : `Nothing to preview: ${(r as any).reason ?? "unknown"}`,
        );
        return;
      }
      setPreviewOf({
        channel,
        kind,
        subject: r.subject ?? "",
        text: r.textPreview ?? "",
        truncated: !!r.truncated,
        recipientCount: r.recipientCount ?? 0,
        targetConfigured: !!r.targetConfigured,
      });
    } catch (e: any) {
      toast.error(e?.message ?? "Couldn't build preview");
    }
  }

  /** The only path that actually delivers. Requires an explicit confirmation. */
  async function confirmSend() {
    if (!teamId || !previewOf) return;
    try {
      const r = await sendTest.mutateAsync({
        teamId,
        channel: previewOf.channel,
        kind: previewOf.kind,
        confirm: true,
      });
      if (r.ok) {
        toast.success(locale === "ar" ? "تمّ الإرسال" : "Sent");
        setPreviewOf(null);
      } else {
        toast.warning(
          locale === "ar"
            ? `لم يُرسل: ${(r as any).reason ?? "غير معروف"}`
            : `Not sent: ${(r as any).reason ?? "unknown"}`,
        );
      }
    } catch (e: any) {
      toast.error(e?.message ?? "Couldn't send");
    }
  }

  const emailCount = configQ.data?.emailRecipientCount ?? 0;

  return (
    <div className="mt-6 space-y-3">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {locale === "ar" ? "ملخّصات الفريق" : "Team digests"}
      </div>

      {/* ---------- Slack ---------- */}
      <div className="rounded-xl border bg-card px-4 py-3">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-base">💬</span>
          <div className="font-medium text-sm">Slack</div>
        </div>
        <input
          type="text"
          value={slackChannelId}
          onChange={e => setSlackChannelId(e.target.value)}
          placeholder="C01ABC23DEF"
          className="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm font-mono outline-none focus:border-coral mb-1"
        />
        <p className="text-[10px] text-muted-foreground mb-2">
          {locale === "ar"
            ? "معرّف القناة (Slack → معلومات القناة → الأسفل). يحتاج إلى توصيل Slack أولاً."
            : "Channel ID (Slack → channel details → bottom). Requires team owner to have Slack connected above."}
        </p>
        <div className="flex items-center gap-3 flex-wrap text-sm">
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={slackDaily} onChange={e => setSlackDaily(e.target.checked)} className="accent-coral" />
            {locale === "ar" ? "يومي" : "Daily"}
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={slackWeekly} onChange={e => setSlackWeekly(e.target.checked)} className="accent-coral" />
            {locale === "ar" ? "أسبوعي" : "Weekly"}
          </label>
          <div className="ms-auto flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => openPreview("slack", "daily")}
              disabled={preview.isPending || !slackChannelId.trim()}
              className="text-[11px] underline text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:no-underline"
            >
              {locale === "ar" ? "معاينة يومية" : "Preview daily"}
            </button>
            <span className="text-muted-foreground/40">·</span>
            <button
              type="button"
              onClick={() => openPreview("slack", "weekly")}
              disabled={preview.isPending || !slackChannelId.trim()}
              className="text-[11px] underline text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:no-underline"
            >
              {locale === "ar" ? "معاينة أسبوعية" : "Preview weekly"}
            </button>
          </div>
        </div>
      </div>

      {/* ---------- Email ---------- */}
      <div className="rounded-xl border bg-card px-4 py-3">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-base">📧</span>
          <div className="font-medium text-sm">
            {locale === "ar" ? "بريد إلكتروني" : "Email"}
          </div>
          {emailCount > 0 && (
            <span className="text-[10px] text-muted-foreground">
              {emailCount} {locale === "ar" ? "مستلم" : emailCount === 1 ? "recipient" : "recipients"}
            </span>
          )}
        </div>
        {/* No address entry: the audience is the team itself, resolved on the
            server at send time. Removing someone from the team removes them
            from the digest, and no address outside the team can be added. */}
        <p className="text-[10px] text-muted-foreground mb-2">
          {locale === "ar"
            ? "يُرسل إلى أعضاء الفريق الحاليين فقط، حسب عناوينهم في حساباتهم. لا يمكن إضافة عناوين خارجية. وبإزالة عضو من الفريق يتوقّف وصول الملخّص إليه."
            : "Goes to current team members only, at the address on their account. External addresses can't be added. Remove someone from the team and they stop receiving it."}
        </p>
        <div className="flex items-center gap-3 flex-wrap text-sm">
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={emailDaily} onChange={e => setEmailDaily(e.target.checked)} className="accent-coral" />
            {locale === "ar" ? "يومي" : "Daily"}
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={emailWeekly} onChange={e => setEmailWeekly(e.target.checked)} className="accent-coral" />
            {locale === "ar" ? "أسبوعي" : "Weekly"}
          </label>
          <div className="ms-auto flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => openPreview("email", "daily")}
              disabled={preview.isPending || emailCount === 0}
              className="text-[11px] underline text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:no-underline"
            >
              {locale === "ar" ? "معاينة يومية" : "Preview daily"}
            </button>
            <span className="text-muted-foreground/40">·</span>
            <button
              type="button"
              onClick={() => openPreview("email", "weekly")}
              disabled={preview.isPending || emailCount === 0}
              className="text-[11px] underline text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:no-underline"
            >
              {locale === "ar" ? "معاينة أسبوعية" : "Preview weekly"}
            </button>
          </div>
        </div>
      </div>

      {/* ---------- Webhook ---------- */}
      <div className="rounded-xl border bg-card px-4 py-3">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-base">🔌</span>
          <div className="font-medium text-sm">
            {locale === "ar" ? "ويب-هوك" : "Webhook"}
          </div>
        </div>
        <input
          type="url"
          value={webhookUrl}
          onChange={e => setWebhookUrl(e.target.value)}
          placeholder="https://hooks.slack.com/services/..."
          className="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm font-mono outline-none focus:border-coral mb-1"
        />
        <p className="text-[10px] text-muted-foreground mb-2">
          {locale === "ar"
            ? "عنوان https يقبل POST بصيغة JSON. كل طلب موقَّع بـ HMAC-SHA256، ولا يُرسل أي محتوى بدون توقيع."
            : "An https URL accepting JSON POSTs. Every request is signed with HMAC-SHA256, and nothing is sent unsigned."}
        </p>
        {!!webhookUrl.trim() && configQ.data && !configQ.data.webhookSigningReady && (
          <p className="text-[10px] text-amber-600 mb-2">
            {locale === "ar"
              ? "لا يوجد مفتاح توقيع مُعدّ. لن يُرسل هذا الويب-هوك حتى يُضبط."
              : "No signing key is configured. This webhook will not send until one is set."}
          </p>
        )}
        <div className="flex items-center gap-3 flex-wrap text-sm">
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={webhookDaily} onChange={e => setWebhookDaily(e.target.checked)} className="accent-coral" />
            {locale === "ar" ? "يومي" : "Daily"}
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={webhookWeekly} onChange={e => setWebhookWeekly(e.target.checked)} className="accent-coral" />
            {locale === "ar" ? "أسبوعي" : "Weekly"}
          </label>
          <div className="ms-auto flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => openPreview("webhook", "daily")}
              disabled={preview.isPending || !webhookUrl.trim()}
              className="text-[11px] underline text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:no-underline"
            >
              {locale === "ar" ? "معاينة يومية" : "Preview daily"}
            </button>
            <span className="text-muted-foreground/40">·</span>
            <button
              type="button"
              onClick={() => openPreview("webhook", "weekly")}
              disabled={preview.isPending || !webhookUrl.trim()}
              className="text-[11px] underline text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:no-underline"
            >
              {locale === "ar" ? "معاينة أسبوعية" : "Preview weekly"}
            </button>
          </div>
        </div>
      </div>

      {/* Preview panel — nothing has been sent at this point. Real delivery
          happens only when the person presses the button below, which says
          exactly what it will do. */}
      {previewOf && (
        <div className="rounded-xl border border-coral/40 bg-card px-4 py-3">
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="font-medium text-sm">
              {locale === "ar" ? "معاينة (لم يُرسل شيء)" : "Preview (nothing sent yet)"}
            </div>
            <button
              type="button"
              onClick={() => setPreviewOf(null)}
              className="text-[11px] underline text-muted-foreground hover:text-foreground"
            >
              {locale === "ar" ? "إغلاق" : "Close"}
            </button>
          </div>
          {!!previewOf.subject && (
            <div className="text-[11px] text-muted-foreground mb-1">
              {previewOf.subject}
            </div>
          )}
          <pre className="whitespace-pre-wrap text-[11px] leading-relaxed bg-muted/40 rounded-md p-2 max-h-52 overflow-auto">
            {previewOf.text}
            {previewOf.truncated ? "\n…" : ""}
          </pre>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <Button
              size="sm"
              variant="outline"
              onClick={confirmSend}
              disabled={sendTest.isPending || !previewOf.targetConfigured}
              className="gap-1.5"
            >
              {sendTest.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
              {previewOf.channel === "email"
                ? locale === "ar"
                  ? `إرسال فعلي إلى ${previewOf.recipientCount} من الأعضاء`
                  : `Send for real to ${previewOf.recipientCount} member${previewOf.recipientCount === 1 ? "" : "s"}`
                : locale === "ar"
                  ? "إرسال فعلي الآن"
                  : "Send for real now"}
            </Button>
            {!previewOf.targetConfigured && (
              <span className="text-[10px] text-muted-foreground">
                {locale === "ar"
                  ? "الوجهة غير مكتملة الإعداد."
                  : "This channel isn't fully configured yet."}
              </span>
            )}
          </div>
        </div>
      )}

      <Button
        size="sm"
        onClick={save}
        disabled={setConfig.isPending}
        className="bg-coral hover:bg-coral/90 text-white gap-1.5"
      >
        {setConfig.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
        {locale === "ar" ? "حفظ الإعدادات" : "Save all"}
      </Button>
    </div>
  );
}

const WEBHOOK_SECRET_FIELD: Record<
  Exclude<Provider, "exchange_on_prem">,
  "signingSecret" | "channelToken" | "clientState"
> = {
  github: "signingSecret",
  slack: "signingSecret",
  google_calendar: "channelToken",
  microsoft: "clientState",
};

const WEBHOOK_FIELD_LABEL: Record<"signingSecret" | "channelToken" | "clientState", string> = {
  signingSecret: "Signing secret",
  channelToken: "Channel token",
  clientState: "Client state",
};

/**
 * Inline editor for per-integration webhook secrets. The current secret value
 * is never returned to the client (server only exposes a `webhookConfigured`
 * boolean), so the textbox is empty until the user pastes a new value.
 */
function WebhookSecretEditor({
  provider,
  integrationId,
  configured,
  onChanged,
}: {
  provider: Provider;
  integrationId: number;
  configured: boolean;
  onChanged: () => void;
}) {
  const { locale } = useI18n();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const setSecret = trpc.integrations.setWebhookSecrets.useMutation({
    onSuccess: () => {
      setValue("");
      onChanged();
      toast.success(
        locale === "ar" ? "تم حفظ سر الويب-هوك" : "Webhook secret saved",
      );
    },
    onError: e => toast.error(e.message),
  });
  // EWS has no inbound webhook surface. Early-return AFTER all hooks
  // (Rules of Hooks) — returning before them crashes the render.
  if (provider === "exchange_on_prem") return null;
  const field = WEBHOOK_SECRET_FIELD[provider as Exclude<Provider, "exchange_on_prem">];
  const url =
    typeof window !== "undefined"
      ? `${window.location.origin}/api/webhooks/${provider}/${integrationId}`
      : `/api/webhooks/${provider}/${integrationId}`;

  return (
    <div className="mt-3 border-t border-border/60 pt-3">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5"
      >
        <Webhook className="size-3.5" />
        {locale === "ar" ? "إعداد ويب-هوك" : "Webhook"}
        <span className="text-[10px] opacity-70">
          {configured
            ? locale === "ar"
              ? "(مكتمل)"
              : "(configured)"
            : locale === "ar"
              ? "(غير مكتمل)"
              : "(not set)"}
        </span>
      </button>

      {open && field && (
        <div className="mt-3 space-y-2">
          <div>
            <div className="text-xs text-muted-foreground mb-1">
              {locale === "ar" ? "رابط الويب-هوك" : "Webhook URL"}
            </div>
            <Input value={url} readOnly className="font-mono text-xs" />
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">
              {WEBHOOK_FIELD_LABEL[field]}
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="password"
                placeholder={
                  configured
                    ? locale === "ar"
                      ? "بدّل القيمة"
                      : "Replace value"
                    : locale === "ar"
                      ? "الصق القيمة"
                      : "Paste value"
                }
                value={value}
                onChange={e => setValue(e.target.value)}
                className="font-mono text-xs"
              />
              <Button
                size="sm"
                disabled={!value || setSecret.isPending}
                onClick={() =>
                  setSecret.mutate({
                    integrationId,
                    [field]: value,
                  } as Parameters<typeof setSecret.mutate>[0])
                }
              >
                {setSecret.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  locale === "ar" ? "حفظ" : "Save"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Inline credential form for the on-prem Exchange (EWS) provider. Posts to
 * `POST /api/integrations/exchange_on_prem/connect`, which validates the
 * credentials by issuing a 1-row FindItem against the inbox and stores the
 * result encrypted at rest.
 *
 * The form intentionally collects no telemetry — credentials never leave the
 * server-side handler. The dialog warns users to prefer a service account /
 * app password over their personal mailbox password.
 */
function ExchangeConnectForm({ onConnected }: { onConnected: () => void }) {
  const { locale } = useI18n();
  const [serverUrl, setServerUrl] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!serverUrl || !username || !password) return;
    setBusy(true);
    try {
      const res = await fetch("/api/integrations/exchange_on_prem/connect", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ serverUrl, username, password }),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
        toast.error(j.message || j.error || "Couldn't connect to Exchange");
        return;
      }
      toast.success(locale === "ar" ? "تم الربط بـ Exchange" : "Connected to Exchange");
      setPassword("");
      onConnected();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-3 border-t border-border/60 pt-3 space-y-2">
      <div className="text-xs text-muted-foreground">
        {locale === "ar"
          ? "Exchange الذاتي الاستضافة عبر EWS. يفضّل استخدام حساب خدمة أو كلمة مرور تطبيق."
          : "Self-hosted Exchange via EWS. Prefer a service account or app password over your personal credentials."}
      </div>
      <Input
        placeholder="https://mail.example.com/EWS/Exchange.asmx"
        value={serverUrl}
        onChange={e => setServerUrl(e.target.value)}
        className="font-mono text-xs"
      />
      <Input
        placeholder={locale === "ar" ? "اسم المستخدم (acme\\jdoe)" : "Username (acme\\jdoe or jdoe@acme.com)"}
        value={username}
        onChange={e => setUsername(e.target.value)}
      />
      <Input
        type="password"
        placeholder={locale === "ar" ? "كلمة المرور / كلمة مرور التطبيق" : "Password / app password"}
        value={password}
        onChange={e => setPassword(e.target.value)}
      />
      <Button
        size="sm"
        onClick={submit}
        disabled={!serverUrl || !username || !password || busy}
        className="bg-coral hover:bg-coral/90 text-white"
      >
        {busy ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : <Plug className="me-2 h-3.5 w-3.5" />}
        {locale === "ar" ? "ربط Exchange" : "Connect Exchange"}
      </Button>
    </div>
  );
}
